import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Location, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { Table } from '@cureskin/api-client';
import { ConnectionService } from '@services/connection-service';
import { RegimenService } from '@services/regimen-service/regimen.service';
import { InstructionsService } from '@services/instructions-service/instructions.service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { CurrentComponentService } from '@services/current-component';
import { ExperimentService } from '@services/experiment/experiment.service';
import { ChatProgressService } from '@services/chat-progress-service/chat-progress.service';
import { AppConfig } from 'client/app/app.config';
import { ToolbarTabsModule } from '@components/toolbar-tabs';
import { CsIconModule } from '@components/icon/icon.module';
import { LoadingModule } from '@components/loading';
import { ConsultationChatHeaderComponent } from '@shared/consultation-chat-header/consultation-chat-header.component';
import { InstructionHeaderComponent } from '@shared/instruction-header/instruction-header.component';
import { ChatProgressBarComponent } from '@shared/chat-progress-bar/chat-progress-bar.component';
import { ProcessedRegimen, RegimenTabClass, RegimenTabLabel, RegimenViewState, TabChangeEvent, TabData } from '../regimen.types';
import { processRegimen, getDisplayState } from '../regimen.utils';
import {
  REGIMEN_ROUTE_PATHS, REGIMEN_TAB_TYPES, REGIMEN_QUERY_PARAMS, REGIMEN_STATUS_BAR_COLOR, REGIMEN_VIEW_STATES,
} from '../regimen.constants';

@Component({
  selector: 'regimen-view-shell',
  standalone: true,
  imports: [RouterModule, ToolbarTabsModule, CsIconModule, LoadingModule, NgTemplateOutlet,
    ConsultationChatHeaderComponent, InstructionHeaderComponent, ChatProgressBarComponent],
  templateUrl: './regimen-view-shell.component.html',
  styles: ':host { @apply tw-block tw-h-full; }',
})
export class RegimenShellComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly conn = inject(ConnectionService);
  private readonly regimenService = inject(RegimenService);
  private readonly appConfig = inject(AppConfig);
  private readonly instructionsService = inject(InstructionsService);
  private readonly appBridge = inject(AppWebBridgeService);
  private readonly currentComponentService = inject(CurrentComponentService);
  private readonly experimentService = inject(ExperimentService);
  readonly chatProgressService = inject(ChatProgressService);
  private readonly destroyRef = inject(DestroyRef);

  readonly FACE = this.appConfig.Shared.Regimen.Class.FACE;
  readonly HAIR = this.appConfig.Shared.Regimen.Class.HAIR;
  readonly TAB_TYPES = REGIMEN_TAB_TYPES;

  processedRegimens: ProcessedRegimen[] = [];
  tabData: TabData = { count: 0, types: [], selectedTab: 0 };
  loading = true;
  hasAddOnInstructions = false;
  headerTitle = '';

  /** Per-class view state — computed once during init */
  classViewStates: Partial<Record<RegimenClass, RegimenViewState>> = {};
  /** Classes eligible for tabs on the current page */
  eligibleClasses: RegimenClass[] = [];
  showTabs = false;
  showHeader = true;
  /** Ordered labels for visible tabs — used to project content into sequential slots */
  visibleTabLabels: RegimenTabLabel[] = [];
  showTreatmentReportTabs = false;

  /** chat-route state (FACE/HAIR fields declared after FACE/HAIR so initializer resolves) */
  isChatRoute = false;
  chatRegimenClass: RegimenClass = this.FACE;
  chatConsultationSession: Table.ConsultationSession | null = null;
  showInstructionPill = false;

  async ngOnInit(): Promise<void> {
    const rawRegimens = await this.regimenService.getRegimens();
    this.processedRegimens = rawRegimens.map(r => processRegimen(r));

    await Promise.all([this.checkAddOnInstructions(), this.buildClassViewStates()]);

    this.refreshForChildRoute();

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.refreshForChildRoute());

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const regimenClass = params[REGIMEN_QUERY_PARAMS.CLASS] as RegimenTabClass | undefined;
      if (regimenClass) this.updateSelectedTab(regimenClass);
    });

    this.loading = false;
    this.appBridge.setStatusBarColor(REGIMEN_STATUS_BAR_COLOR);
  }

  /** Recompute the child-route-derived view state (header + tabs). Runs on init and on every
   *  child-route navigation so the tab set always matches the currently rendered child. */
  private refreshForChildRoute(): void {
    const childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    this.showHeader = childPath !== REGIMEN_ROUTE_PATHS.PREPARATION;
    this.isChatRoute = childPath === REGIMEN_ROUTE_PATHS.CHAT;
    this.showInstructionPill = (childPath === REGIMEN_ROUTE_PATHS.INSTRUCTIONS
      || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS)
      && this.experimentService.instructionsLanguageChangeExperiment;
    this.computeTabVisibility(childPath);
    this.setTabData();
    this.updateHeaderTitle(childPath);
    if (this.isChatRoute) void this.loadChatConsultationSession();
  }

  private async loadChatConsultationSession(): Promise<void> {
    this.chatRegimenClass = (this.route.firstChild?.snapshot?.queryParams?.[REGIMEN_QUERY_PARAMS.CLASS] as RegimenClass)
      || this.FACE;
    this.chatConsultationSession = await this.conn.findConsultationSession(this.chatRegimenClass);
  }

  tabChange(event: TabChangeEvent): void {
    if (event.type === REGIMEN_TAB_TYPES.OTHERS) {
      this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, REGIMEN_ROUTE_PATHS.INSTRUCTIONS], {
        queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: REGIMEN_TAB_TYPES.OTHERS },
        replaceUrl: true,
      });
      return;
    }
    if (this.showTreatmentReportTabs) {
      const targetPath = event.index === 1 ? REGIMEN_ROUTE_PATHS.REPORT : REGIMEN_ROUTE_PATHS.UNPAID;
      const [selectedClass] = this.eligibleClasses;
      if (!selectedClass) return;
      this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, targetPath], {
        queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: selectedClass },
        replaceUrl: true,
      });
      return;
    }
    // Map tab index → eligible class (fixed order: FACE at 0, HAIR at 1)
    const regimenClass = this.eligibleClasses[event.index];
    if (!regimenClass) return;

    let childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    // If currently on addon-instructions, switch back to instructions
    if (childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS) {
      childPath = REGIMEN_ROUTE_PATHS.INSTRUCTIONS;
    }
    this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, childPath], {
      queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: regimenClass },
      replaceUrl: true,
    });
  }

  back(): void {
    const handler = this.currentComponentService?.component?.handleBackPress;
    if (handler) {
      handler().catch((): void => undefined);
      return;
    }
    this.location.back();
  }

  private updateHeaderTitle(childPath: string | undefined): void {
    if (this.showTreatmentReportTabs) {
      this.headerTitle = '';
      return;
    }
    if (childPath === REGIMEN_ROUTE_PATHS.INSTRUCTIONS || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS) {
      this.headerTitle = $localize`:@@instructions:Instructions`;
    } else {
      this.headerTitle = $localize`:@@getYourTreatment:Get your treatment kit`;
    }
  }

  /** Compute and store the view state for each regimen class */
  private async buildClassViewStates(): Promise<void> {
    for (const regimenClass of [this.FACE, this.HAIR]) {
      const regimen = this.processedRegimens.find(r => r.class === regimenClass) ?? null;
      let consultationSession: { get(key: string): unknown } | null = null;
      if (!regimen?.objectId) {
        consultationSession = await this.conn.findConsultationSession(regimenClass);
      }
      this.classViewStates[regimenClass] = getDisplayState(regimen, { consultationSession });
    }
  }

  /** Compute which tabs to show based on the current child route */
  private computeTabVisibility(childPath: string | undefined): void {
    this.eligibleClasses = this.getEligibleClasses(childPath);
    const isInstructionPage = childPath === REGIMEN_ROUTE_PATHS.INSTRUCTIONS
      || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS;

    this.showTreatmentReportTabs = this.isTreatmentReportRoute(childPath)
      && this.eligibleClasses.length === 1
      && this.experimentService.reportInRegimenPageExperiment;

    this.visibleTabLabels = [];
    if (this.showTreatmentReportTabs) {
      this.visibleTabLabels.push(REGIMEN_TAB_TYPES.TREATMENT, REGIMEN_TAB_TYPES.REPORT);
    } else {
      if (this.eligibleClasses.includes(this.FACE)) this.visibleTabLabels.push(this.FACE);
      if (this.eligibleClasses.includes(this.HAIR)) this.visibleTabLabels.push(this.HAIR);
      if (this.hasAddOnInstructions && isInstructionPage) this.visibleTabLabels.push(REGIMEN_TAB_TYPES.OTHERS);
    }

    this.showTabs = this.visibleTabLabels.length >= 2;
  }

  private isTreatmentReportRoute(childPath: string | undefined): boolean {
    return childPath === REGIMEN_ROUTE_PATHS.UNPAID || childPath === REGIMEN_ROUTE_PATHS.REPORT;
  }

  /** Get eligible classes for a given child route */
  private getEligibleClasses(childPath: string | undefined): RegimenClass[] {
    const classes = [this.FACE, this.HAIR];

    switch (childPath) {
      case REGIMEN_ROUTE_PATHS.INSTRUCTIONS:
      case REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS:
        return classes.filter(cls =>
          this.classViewStates[cls] === REGIMEN_VIEW_STATES.INSTRUCTION
          || this.classViewStates[cls] === REGIMEN_VIEW_STATES.UNPAID,
        );
      case REGIMEN_ROUTE_PATHS.EMPTY:
        return classes.filter(cls => this.classViewStates[cls] === REGIMEN_VIEW_STATES.EMPTY);
      case REGIMEN_ROUTE_PATHS.UNPAID:
      case REGIMEN_ROUTE_PATHS.REPORT:
        return classes.filter(cls => this.classViewStates[cls] === REGIMEN_VIEW_STATES.UNPAID);
      case REGIMEN_ROUTE_PATHS.CHAT:
        return classes.filter(cls => this.classViewStates[cls] === REGIMEN_VIEW_STATES.CHAT);
      default:
        return [];
    }
  }

  private setTabData(): void {
    if (!this.showTabs) {
      this.tabData = { count: 0, types: [], selectedTab: 0 };
      return;
    }

    const types: string[] = this.visibleTabLabels.map(label =>
      label === REGIMEN_TAB_TYPES.OTHERS ? REGIMEN_TAB_TYPES.OTHERS : REGIMEN_TAB_TYPES.REGIMEN,
    );

    const queryClass = this.route.firstChild?.snapshot?.queryParams?.[REGIMEN_QUERY_PARAMS.CLASS] as RegimenTabClass | undefined;
    const childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    const selectedTab = this.computeSelectedTabIndex(queryClass, childPath);

    this.tabData = { count: types.length, types, selectedTab };
  }

  private updateSelectedTab(regimenClass: RegimenTabClass): void {
    const childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    const idx = this.computeSelectedTabIndex(regimenClass, childPath);
    this.tabData = { ...this.tabData, selectedTab: idx };
  }

  /** Resolve the active tab index based on the current class query param and child route */
  private computeSelectedTabIndex(queryClass: RegimenTabClass | undefined, childPath: string | undefined): number {
    if (this.showTreatmentReportTabs) return childPath === REGIMEN_ROUTE_PATHS.REPORT ? 1 : 0;
    if (queryClass === REGIMEN_TAB_TYPES.OTHERS || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS) {
      return Math.max(0, this.visibleTabLabels.indexOf(REGIMEN_TAB_TYPES.OTHERS));
    }
    const regimenClass = queryClass ?? this.FACE;
    if (regimenClass !== this.FACE && regimenClass !== this.HAIR) return 0;
    return Math.max(0, this.visibleTabLabels.indexOf(regimenClass));
  }

  private async checkAddOnInstructions(): Promise<void> {
    const user = this.conn.getActingUser();
    const cachedAddOn = this.instructionsService.getAddOnInstructions();
    if (cachedAddOn?.length > 0) {
      this.hasAddOnInstructions = true;
    } else if (user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED) {
      const instructions = await this.conn.getInstructionsForUser();
      if (instructions) this.instructionsService.setUserInstructions(instructions);
      this.hasAddOnInstructions = (instructions?.userInstructions?.ADDITIONAL_PRODUCTS?.length ?? 0) > 0;
    }
  }
}
